import type { OrderRecord } from "@/lib/utils";

// ----------------------------------------------------------------------------

interface DashboardChartPoint {
  name: string;
  value: number;
}

interface DashboardMetrics {
  revenue: number;
  ordersCount: number;
  activeClientsCount: number;
  averageOrderValue: number;
}

interface DateRange {
  startDate: string;
  endDate: string;
}

const MONTH_LABELS = [
  "Gen",
  "Feb",
  "Mar",
  "Apr",
  "Mag",
  "Giu",
  "Lug",
  "Ago",
  "Set",
  "Ott",
  "Nov",
  "Dic",
];

// ----------------------------------------------------------------------------

export function parseCurrencyValue(
  value: string | number | undefined | null,
): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const raw = String(value).trim();
  if (!raw) return 0;

  const cleaned = raw.replace(/[^\d,.-]/g, "");
  if (!cleaned) return 0;

  if (cleaned.includes(",") && cleaned.includes(".")) {
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    if (lastComma > lastDot) {
      // European format: 1.234,56
      const normalized = cleaned.replace(/\./g, "").replace(",", ".");
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    } else {
      // US format: 1,234.56
      const normalized = cleaned.replace(/,/g, "");
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }

  if (cleaned.includes(",")) {
    const normalized = cleaned.replace(",", ".");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatCurrencyValue(value: number): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function getDashboardMetrics(orders: OrderRecord[]): DashboardMetrics {
  const revenue = orders.reduce(
    (total, order) => total + parseCurrencyValue(order.total),
    0,
  );

  const activeClientKeys = new Set(
    orders
      .map((order) => order.clientId || order.email || order.customerName)
      .filter(Boolean),
  );

  const ordersCount = orders.length;
  const activeClientsCount = activeClientKeys.size;
  const averageOrderValue = ordersCount > 0 ? revenue / ordersCount : 0;

  return {
    revenue,
    ordersCount,
    activeClientsCount,
    averageOrderValue,
  };
}

export function getPreviousDateRange(range: DateRange): DateRange {
  const start = new Date(`${range.startDate}T00:00:00`);
  const end = new Date(`${range.endDate}T00:00:00`);
  const millisecondsInDay = 24 * 60 * 60 * 1000;
  const periodLength = Math.max(
    1,
    Math.floor((end.getTime() - start.getTime()) / millisecondsInDay) + 1,
  );

  const previousEnd = new Date(start.getTime() - millisecondsInDay);
  const previousStart = new Date(
    previousEnd.getTime() - (periodLength - 1) * millisecondsInDay,
  );

  return {
    startDate: toDateInputValue(previousStart),
    endDate: toDateInputValue(previousEnd),
  };
}

export function formatDeltaText(
  currentValue: number,
  previousValue: number,
  fallbackLabel: string,
): string {
  if (previousValue === 0) {
    if (currentValue === 0) {
      return fallbackLabel;
    }

    return "Nuovi dati rispetto al periodo precedente";
  }

  const delta = ((currentValue - previousValue) / previousValue) * 100;
  const direction = delta >= 0 ? "+" : "";

  return `${direction}${delta.toFixed(1)}% vs periodo precedente`;
}

export function buildMonthlyRevenueData(
  orders: OrderRecord[],
  range: DateRange,
): DashboardChartPoint[] {
  const monthKeys = getMonthKeysInRange(range.startDate, range.endDate);
  const revenueByMonth = new Map<string, number>();

  monthKeys.forEach((monthKey) => {
    revenueByMonth.set(monthKey, 0);
  });

  orders.forEach((order) => {
    if (!order.date) {
      return;
    }

    const monthKey = order.date.slice(0, 7);

    if (!revenueByMonth.has(monthKey)) {
      return;
    }

    revenueByMonth.set(
      monthKey,
      (revenueByMonth.get(monthKey) ?? 0) + parseCurrencyValue(order.total),
    );
  });

  return monthKeys.map((monthKey) => {
    const [year, month] = monthKey.split("-");
    const monthIndex = Number(month) - 1;
    const label = MONTH_LABELS[monthIndex] ?? monthKey;

    return {
      name: monthKeys.length > 12 ? `${label} ${year?.slice(-2) ?? ""}` : label,
      value: revenueByMonth.get(monthKey) ?? 0,
    };
  });
}

function getMonthKeysInRange(startDate: string, endDate: string): string[] {
  const [startYear, startMonth] = startDate.split("-").map(Number);
  const [endYear, endMonth] = endDate.split("-").map(Number);

  if (!startYear || !startMonth || !endYear || !endMonth) {
    return [];
  }

  const cursor = new Date(startYear, startMonth - 1, 1);
  const limit = new Date(endYear, endMonth - 1, 1);
  const monthKeys: string[] = [];

  while (cursor <= limit) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    monthKeys.push(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return monthKeys;
}

function toDateInputValue(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}
